import {
  arrayRemove,
  arrayUnion,
  doc,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { useMemo } from 'react';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { firestore } from '@/lib/firebase';
import { TV_CONTROLLER_COLLECTION } from '../schema/tvController';
import {
  normalizeGroups,
  TV_CONTROLLER_GROUP_COLLECTION,
  TV_CONTROLLER_GROUP_TREE_DOC,
  TvControllerGroup,
} from '../schema/tvControllerGroup';

const treeRef = () =>
  doc(firestore, TV_CONTROLLER_GROUP_COLLECTION, TV_CONTROLLER_GROUP_TREE_DOC);

// the manually managed group tree (empty until the first group is created)
export function useTvControllerGroups(): [TvControllerGroup[], boolean] {
  const [data, loading] = useDocumentData(treeRef(), {
    snapshotListenOptions: { includeMetadataChanges: false },
  });
  const groups = useMemo(
    () => (data?.nodes || []) as TvControllerGroup[],
    [data]
  );
  return [groups, loading];
}

export async function saveTvControllerGroups(nodes: TvControllerGroup[]) {
  await setDoc(treeRef(), { nodes: normalizeGroups(nodes) });
}

export type TagChange = {
  tag: string;
  addIds?: string[];
  removeIds?: string[];
};

// Save the tree and re-stamp member TVs' tags in one atomic batch, so the
// tree and the tags it points at never disagree.
export async function saveTvControllerGroupsWithTags(
  nodes: TvControllerGroup[],
  changes: TagChange[]
) {
  const batch = writeBatch(firestore);
  batch.set(treeRef(), { nodes: normalizeGroups(nodes) });
  changes.forEach(({ tag, addIds = [], removeIds = [] }) => {
    addIds.forEach((id) => {
      batch.update(doc(firestore, TV_CONTROLLER_COLLECTION, id), {
        tags: arrayUnion(tag),
      });
    });
    removeIds.forEach((id) => {
      batch.update(doc(firestore, TV_CONTROLLER_COLLECTION, id), {
        tags: arrayRemove(tag),
      });
    });
  });
  await batch.commit();
}
